export type FaqCategoryId =
  | 'account'
  | 'payment'
  | 'product'
  | 'guarantees'
  | 'help';

export type FaqItem = {
  category: FaqCategoryId;
  question: string;
  answer: string;
};

export const FAQ_CATEGORIES: { id: FaqCategoryId; label: string; icon: string }[] = [
  { id: 'account', label: 'Managing your account', icon: '/assets/icons/faqs/ic_account.svg' },
  { id: 'payment', label: 'Payment', icon: '/assets/icons/faqs/ic_payment.svg' },
  { id: 'product', label: 'Problem with the Product', icon: '/assets/icons/faqs/ic_package.svg' },
  { id: 'guarantees', label: 'Guarantees', icon: '/assets/icons/faqs/ic_assurances.svg' },
  { id: 'help', label: 'General Help', icon: '/assets/icons/faqs/icon.png' },
];

export const FAQ_ITEMS: FaqItem[] = [
  // ── Managing your account ─────────────────────────────────────────────
  {
    category: 'account',
    question: 'How do I invite people to my organization?',
    answer:
      'Go to Users in the side nav, click "Invite user", and enter their email. They\'ll get a sign-up link tied to your organization.',
  },
  {
    category: 'account',
    question: 'How do I change my password?',
    answer:
      'Open your profile from the top-right avatar menu, scroll to the security section, and use "Change password". You\'ll need your current password to confirm.',
  },
  {
    category: 'account',
    question: 'How do I switch between organizations?',
    answer:
      'Super admins and members of multiple organizations can switch from the org selector in the top header. Your view, dashboards, and data update automatically.',
  },

  // ── Payment ────────────────────────────────────────────────────────────
  {
    category: 'payment',
    question: 'What payment methods do you accept?',
    answer:
      'We use Stripe for all payments and accept major credit and debit cards. Invoices are issued automatically after each billing cycle.',
  },
  {
    category: 'payment',
    question: 'How does per-seat pricing work?',
    answer:
      'You\'re billed for the number of active users in your organization at the start of each billing cycle. Adding seats mid-cycle is prorated automatically.',
  },
  {
    category: 'payment',
    question: 'Where can I download invoices?',
    answer:
      'Open Settings → Billing. Each past invoice has a "Download" link that opens the Stripe-hosted PDF receipt.',
  },

  // ── Problem with the Product ───────────────────────────────────────────
  {
    category: 'product',
    question: 'A page is blank or showing an error - what should I do?',
    answer:
      'First, try a hard refresh (Ctrl+Shift+R). If the issue persists, take a screenshot of the page and the browser console, and send it to support - include the URL.',
  },
  {
    category: 'product',
    question: 'A skill or topic I just added isn\'t showing up.',
    answer:
      'New items appear immediately, but stale browser caches occasionally hide them. Try refreshing the page. If it still doesn\'t appear, contact support with the skill name.',
  },
  {
    category: 'product',
    question: 'My evaluation progress looks wrong.',
    answer:
      'Progress is recalculated whenever you check or uncheck a sub-topic. If the percentage looks off, refresh and re-check the items. Persistent issues should be reported to support.',
  },

  // ── Guarantees ─────────────────────────────────────────────────────────
  {
    category: 'guarantees',
    question: 'Can I cancel any time?',
    answer:
      'Yes. Cancel from Settings → Billing. Your plan stays active until the end of the current billing period.',
  },
  {
    category: 'guarantees',
    question: 'Is my organization\'s data secure?',
    answer:
      'All data is encrypted in transit and at rest. Authentication uses short-lived JWTs with refresh tokens; payments are handled directly by Stripe - we never store card numbers.',
  },
  {
    category: 'guarantees',
    question: 'What happens to my data if I cancel?',
    answer:
      'Your data stays available in read-only mode for 30 days after cancellation, so you can export anything you need. After that, it\'s permanently deleted.',
  },

  // ── General Help ───────────────────────────────────────────────────────
  {
    category: 'help',
    question: 'How do I get started?',
    answer:
      'After signing up, create your first skill in the Skill Builder, add a few topics, then invite your team. Everyone\'s dashboard fills in as they complete evaluations.',
  },
  {
    category: 'help',
    question: 'Is there a mobile app?',
    answer:
      'Not yet. The web app is fully responsive, so it works on phones and tablets. A native mobile app is on the roadmap.',
  },
  {
    category: 'help',
    question: 'How do I contact support?',
    answer:
      'Use the contact form below, or email support@techstack.io directly. We aim to respond within one business day.',
  },
];
